import { Button } from 'react-bootstrap';
import _ from 'lodash';
import { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import RowDetail from './rowDetailPanelTotal';
import Car from '../Icons/Car';
import SummaryIcon from '../Icons/SummaryIcon';
import { currencyFormatter, decimalFormatter } from '../Table/utility';
import { MonthContext } from '../../context/MonthContext';
import { UserContext } from '../../context/UserContext';
import { getWorklogIssueHoursSummary } from '../../utils/API/worklog';
import { DEFAULT_USER_WORKTIME } from '../../constants';
import Expenses from '../Icons/Expenses';
import Bancomat from '../Icons/Bancomat';
import Holiday from '../Icons/Holiday';
import Absence from '../Icons/Absence';
import Leave from '../Icons/Leave';

// Utility per formattare ore e giorni
const formatHours = (hours) => {
  const isInt = Number.isInteger(hours);
  return isInt
    ? hours.toLocaleString('it-IT', { maximumFractionDigits: 0 })
    : hours.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const formatDays = (hours, worktime) => {
  const effectiveWorktime = worktime || DEFAULT_USER_WORKTIME;
  const val = hours / effectiveWorktime;
  const isInt = Number.isInteger(val);
  const formatted = isInt
    ? val.toLocaleString('it-IT', { maximumFractionDigits: 0 })
    : val.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return `${formatted}gg`;
};

const PanelTotal = (props) => {
  const {
    report,
    summaryData,
    handleShowModalConfirm,
    disabled,
    canClose,
    user: userProp,
  } = props;
  const { month } = useContext(MonthContext);
  const { user: userContext } = useContext(UserContext);
  const user = userProp || userContext;

  const [hoursSummary, setHoursSummary] = useState({
    pr_hours_ytd: 0,
    f_hours_ytd: 0,
    absence_hours_next_month_to_year: 0,
    pr_hours_next_month_to_year: 0,
    f_hours_next_month_to_year: 0,
  });

  const generationRef = useRef(0);

  useEffect(() => {
    const generation = ++generationRef.current;

    const fetchHoursSummary = async () => {
      try {
        const data = await getWorklogIssueHoursSummary(
          month.format('YYYY'),
          month.format('MM'),
          user.username
        );
        if (generation === generationRef.current && data) {
          setHoursSummary(data);
        }
      } catch (e) {
        if (generation === generationRef.current) {
          console.warn('Impossibile recuperare il summary delle ore:', e);
        }
      }
    };

    if (user?.username) {
      fetchHoursSummary();
    }
  }, [month, user?.username]);

  const isReportConfirmed = report.confirmed || !report.id;
  const getCost = (key) => _.get(summaryData, key, 0);

  const getHours = () => {
    const { format } = decimalFormatter;
    const hoursLogged = _.get(summaryData, 'total_time_spent', 0);
    const hoursTodayExpected = _.get(summaryData, 'total_today_time', 0);
    const hoursMonthExpected = _.get(summaryData, 'total_time', 0);
    const msg = `${format(hoursLogged)} di ${format(hoursTodayExpected)}`;
    const isFinal = hoursTodayExpected === hoursMonthExpected;
    return isFinal ? (
      msg
    ) : (
      <>
        {msg}
        <br />
        [Tot. ore {format(hoursMonthExpected)}]
      </>
    );
  };

  const hoursStatus = () => {
    const hoursLogged = _.get(summaryData, 'total_time_spent', 0);
    const hoursExpected = _.get(summaryData, 'total_today_time', 0);
    return hoursLogged !== hoursExpected;
  };

  const renderActions = () => {
    if (!isReportConfirmed) {
      if (!canClose) {
        return null;
      }
      return (
        <Button
          className="btn btn-primary"
          onClick={handleShowModalConfirm}
          style={{ width: '100%' }}
          disabled={disabled}
        >
          Chiudi mese
        </Button>
      );
    }
    return (
      <div className="expenses-report-status">
        <div>
          <span className="icon-check" />
          <span>CONFIRMED</span>
        </div>
        {report.notes && (
          <div className="expenses-report-status-note">
            <span>Notes: {report.notes}</span>
          </div>
        )}
      </div>
    );
  };

  const [closed, setClosed] = useState(false);

  return (
    <div className={`sidebar  ${closed ? 'closed' : ''}`}>
      <div className="sidebar__header" style={{ gap: '8px' }}>
        <SummaryIcon />
        {!closed && 'Riepilogo'}
      </div>
      <div className="sidebar__body">
        {!closed && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              paddingTop: '8px',
            }}
          >
            <div className="sidebar__container">
              <p className="sidebar__subtitle-small">Ore</p>
              <RowDetail
                label="Ore loggate"
                value={getHours()}
                valueExtraClasses={hoursStatus() ? ['error'] : null}
                icon="icon-time"
              />
            </div>
            <div className="sidebar__container">
              <p className="sidebar__subtitle-small">Rimborsi</p>
              <RowDetail
                label="Rimborso Km"
                value={currencyFormatter.format(getCost('trips_cost'))}
                icon={<Car />}
              />
              <RowDetail
                label="Rimborso spese"
                value={currencyFormatter.format(getCost('expenses_cost'))}
                icon={<Expenses />}
              />
              <RowDetail
                label="Anticipo"
                value={currencyFormatter.format(getCost('cash_advances'))}
                icon={<Bancomat />}
              />
              <hr className="divider" style={{ margin: '6px' }} />
              <div className="total__details">
                <div className="total__details__label">Totale da liquidare</div>
                <div className="total__details__value">
                  {currencyFormatter.format(getCost('total_due'))}
                </div>
              </div>
            </div>
            <div className="sidebar__container">
              <p
                className="sidebar__subtitle-small"
                style={{ paddingBottom: '6px' }}
              >
                Ferie, permessi e assenze
              </p>
              <div className="sidebar__body-m">
                Godute <span style={{ marginLeft: 8 }} />
                <span className="sidebar__body-xs">
                  (da 1/01 a {month.clone().endOf('month').format('DD/MM')})
                </span>
              </div>
              <RowDetail
                label="Ferie"
                value={
                  hoursSummary.f_hours_ytd === 0
                    ? '--'
                    : `${formatHours(hoursSummary.f_hours_ytd)}h (${formatDays(
                        hoursSummary.f_hours_ytd,
                        user.worktime
                      )})`
                }
                icon={<Holiday />}
              />
              <RowDetail
                label="Permessi"
                value={
                  hoursSummary.pr_hours_ytd === 0
                    ? '--'
                    : `${formatHours(hoursSummary.pr_hours_ytd)}h (${formatDays(
                        hoursSummary.pr_hours_ytd,
                        user.worktime
                      )})`
                }
                icon={<Leave />}
              />
              <div className="sidebar__body-m" style={{ padding: '4px' }}>
                PROGRAMMATE
                <span style={{ marginLeft: 8 }} />
                {month.month() !== 11 && (
                  <span className="sidebar__body-xs">
                    (da{' '}
                    {month
                      .clone()
                      .add(1, 'month')
                      .startOf('month')
                      .format('DD/MM')}{' '}
                    a 31/12)
                  </span>
                )}
              </div>
              <RowDetail
                label="Ferie"
                value={
                  month.month() === 11 ||
                  hoursSummary.f_hours_next_month_to_year === 0
                    ? '--'
                    : `${formatHours(
                        hoursSummary.f_hours_next_month_to_year
                      )}h (${formatDays(
                        hoursSummary.f_hours_next_month_to_year,
                        user.worktime
                      )})`
                }
                icon={<Holiday />}
              />
              <RowDetail
                label="Permessi"
                value={
                  month.month() === 11 ||
                  hoursSummary.pr_hours_next_month_to_year === 0
                    ? '--'
                    : `${formatHours(
                        hoursSummary.pr_hours_next_month_to_year
                      )}h (${formatDays(
                        hoursSummary.pr_hours_next_month_to_year,
                        user.worktime
                      )})`
                }
                icon={<Leave />}
              />
              <RowDetail
                label="Assenze"
                value={
                  month.month() === 11 ||
                  hoursSummary.absence_hours_next_month_to_year === 0
                    ? '--'
                    : `${formatHours(
                        hoursSummary.absence_hours_next_month_to_year
                      )}h (${formatDays(
                        hoursSummary.absence_hours_next_month_to_year,
                        user.worktime
                      )})`
                }
                icon={<Absence />}
              />
            </div>
          </div>
        )}
      </div>
      {!closed && (
        <div
          style={{
            padding: '16px',
            position: 'sticky',
            bottom: '56px',
            zIndex: 2,
            background: '#fff',
          }}
        >
          {renderActions()}
        </div>
      )}
      <div className="sidebar__footer">
        <button
          className="btn btn--light"
          type="button"
          onClick={() => setClosed(!closed)}
        >
          {closed ? (
            <div className="icon-arrow-double-left" />
          ) : (
            <div className="icon-arrow-double-right" />
          )}
          Collapse
        </button>
      </div>
    </div>
  );
};

PanelTotal.defaultProps = {
  report: {},
  summaryData: {},
  handleShowModalConfirm: () => {},
  disabled: false,
  canClose: true,
  user: undefined,
};

PanelTotal.propTypes = {
  report: PropTypes.instanceOf(Object),
  summaryData: PropTypes.instanceOf(Object),
  handleShowModalConfirm: PropTypes.func,
  disabled: PropTypes.bool,
  canClose: PropTypes.bool,
  user: PropTypes.instanceOf(Object),
};

export default PanelTotal;
